import type { ChangeEvent, ComponentType, ReactNode } from 'react';
import { useId } from 'react';
import { StarIcon } from '../../assets/icons/StarIcon';
import { PlusIcon } from '../../assets/icons/PlusIcon';
import {
  ANONYMOUS_REVIEWER_NAME,
  REVIEW_AGE_GROUPS,
  REVIEW_MEDIA_CONTENT_TYPES,
  REVIEW_MEDIA_MAX_COUNT,
  buildReviewCreditBannerCopy,
  formatLoyaltyCredit,
  resolveOrdersReviewCreditStatus,
  resolveProductReviewCreditStatus,
  useRegionCurrency,
  useReviewService,
  useUserService,
  type ReviewAgeGroup,
  type ReviewCreditBannerStatus,
  type ReviewCreditAmounts,
  type ReviewGender,
  type ReviewImageFile,
  type ReviewImagePreview,
  type ReviewMedia,
  type ValidationErrors,
} from '@skin-seoul/service';
import { ReviewVideoPoster } from '../ReviewVideoPoster';
import { ReviewBodyTextarea } from './ReviewBodyTextarea';
import Select from './Select';

const REVIEW_MEDIA_ACCEPT = REVIEW_MEDIA_CONTENT_TYPES.join(',');

const AGE_GROUP_LABELS: Record<ReviewAgeGroup, string> = {
  under_25: 'Under 25',
  '25_34': '25 - 34',
  '35_44': '35 - 44',
  '45_54': '45 - 54',
  '55_60': '55 - 60',
  over_60: 'Over 60',
};

const AGE_GROUP_OPTIONS = REVIEW_AGE_GROUPS.map((ageGroup) => ({
  value: ageGroup,
  label: AGE_GROUP_LABELS[ageGroup],
}));

const GENDER_LABELS: Record<ReviewGender, string> = {
  female: 'Female',
  male: 'Male',
  non_binary: 'Non-binary',
};

// Figma orders the options Male, Female, Non-binary. The contract array is ordered for validation,
// not for display, so the form keeps its own order rather than inheriting it.
const GENDER_DISPLAY_ORDER: ReviewGender[] = ['male', 'female', 'non_binary'];

const GENDER_OPTIONS = GENDER_DISPLAY_ORDER.map((gender) => ({ value: gender, label: GENDER_LABELS[gender] }));
import { Input, RadioGroup, RadioGroupItem, LucideIcon } from '@skin-seoul/design-system';
import {
  Section,
  Star,
  FileInput,
  Row,
  SubmitButton,
  ImageUploadBox,
  Thumbnail,
  ThumbnailImage,
  RemoveButton,
  UploadArea,
  SmallUploadArea,
} from './SharedStyles';
import { decodeHTMLEntities } from '@skin-seoul/utils';

export type ReviewFieldName = keyof ValidationErrors;

/**
 * Marks the section a field's message belongs to, so a refused submit can bring the first offending
 * one into view. An attribute rather than a ref: these helpers return the section elements
 * themselves, and threading a ref into each would mean wrapping every one of them in a new node.
 */
export const reviewFieldAnchor = (field: ReviewFieldName) => ({ 'data-review-field': field });

export const reviewFieldSelector = (field: ReviewFieldName) => `[data-review-field="${field}"]`;

interface ReviewSubmissionAlertProps {
  message: string;
}

/**
 * Inside the section that has to change, above its control; a toast leaves them hunting for it.
 * Every section says its piece through this one, so a refused submit reads the same everywhere.
 */
export const ReviewSubmissionAlert = ({ message }: ReviewSubmissionAlertProps) => (
  <div
    role="alert"
    data-testid="review-submission-alert"
    className="flex flex-row gap-3 items-start w-full rounded-[8px] bg-error-50 px-3 py-2"
  >
    <LucideIcon name="CircleAlert" width="16px" height="16px" className="text-error-600 shrink-0 mt-0.5" />
    <p className="text-label-small font-medium text-error-600">{message}</p>
  </div>
);

interface SectionProps {
  children: ReactNode;
  style?: React.CSSProperties;
}

interface RenderProductIntroProps {
  productInfo: {
    name: string;
    brand: string;
    image: string;
    category: string;
  };
}

interface RenderRatingSectionProps {
  rating: number;
  setRating: (rating: number) => void;
  SectionComponent?: ComponentType<SectionProps>;
  errorMessage?: string;
}

interface RenderSkinInfoSectionProps {
  skinType: string;
  setSkinType: (value: string) => void;
  skinConcern: string;
  setSkinConcern: (value: string) => void;
  skinTypeOptions: Array<{ value: string; label: string }>;
  skinConcernOptions: Array<{ value: string; label: string }>;
  skinTypeError?: string;
  skinConcernError?: string;
}

interface RenderReviewContentSectionProps {
  content: string;
  setContent: (content: string) => void;
  alertMessage?: string | null;
  flaggedText?: string[];
  errorMessage?: string;
}

interface RenderTitleSectionProps {
  title: string;
  setTitle: (title: string) => void;
  errorMessage?: string;
}

interface RenderImageUploadSectionProps {
  images: ReviewImageFile[];
  imagePreviews: ReviewImagePreview[];
  handleImageChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleRemoveImage: (id: string) => void;
  isProcessingImages?: boolean;
  imageUploadError?: string | null;
  /** Below the ceiling on an edit, where kept attachments count against it too. */
  maxAttachments?: number;
  processingMessage?: string;
  pendingVideo?: PendingVideoUpload | null;
  errorMessage?: string;
}

export interface PendingVideoUpload {
  posterUrl: string;
  /** 0 to 1 while the encoder reports; null until it has, which is the bar's indeterminate state. */
  progress: number | null;
}

interface RenderRecommendationSectionProps {
  recommend: string;
  setRecommend: (value: string) => void;
  errorMessage?: string;
}

interface RenderAgeGroupSectionProps {
  ageGroup: string;
  setAgeGroup: (value: string) => void;
  errorMessage?: string;
}

interface RenderGenderSectionProps {
  gender: string;
  setGender: (value: string) => void;
  errorMessage?: string;
}

interface RenderNicknameSectionProps {
  nickname: string;
  setNickname: (nickname: string) => void;
  isReadOnly?: boolean;
  errorMessage?: string;
}

interface RenderEmailSectionProps {
  email: string;
  setEmail: (email: string) => void;
  isReadOnly?: boolean;
  errorMessage?: string;
}

interface RenderSubmitSectionProps {
  isSubmitDisabled: boolean;
  onSubmit: () => void;
  label?: string;
}

interface RenderExistingMediaSectionProps {
  media: ReviewMedia[];
  onRemove: (url: string) => void;
  /** Freeing a kept slot mid-upload cannot widen the ceiling the run already captured. */
  isProcessingImages?: boolean;
}

export const renderProductIntro = ({ productInfo }: RenderProductIntroProps) => (
  <div className="flex flex-row gap-6 items-center w-full">
    {/* eslint-disable-next-line @next/next/no-img-element */}
    <img
      src={productInfo.image}
      alt={productInfo.name}
      className="w-[88px] h-[88px] tablet:w-[136px] tablet:h-[136px] shrink-0 rounded-lg overflow-hidden"
    />
    <div className="flex flex-col items-start gap-2">
      <div className="flex flex-row gap-2 items-center">
        <p className="text-label-medium font-semibold text-neutral-700">{productInfo.brand}</p>
        <p className="text-label-small font-medium text-primary-400">{decodeHTMLEntities(productInfo.category)}</p>
      </div>
      <p className="text-paragraph-small font-normal text-neutral-800">{productInfo.name}</p>
    </div>
  </div>
);

export const renderRatingSection = ({
  rating,
  setRating,
  SectionComponent: _SectionComponent = Section,
  errorMessage,
}: RenderRatingSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full" {...reviewFieldAnchor('rating')}>
    <p className="text-label-medium font-medium">Overall ratings*</p>
    {errorMessage && <ReviewSubmissionAlert message={errorMessage} />}
    <div className="flex flex-row w-full items-center justify-center">
      <div className="flex flex-row items-center gap-1 justify-center">
        {[1, 2, 3, 4, 5].map((num) => (
          <Star
            key={num}
            type="button"
            selected={rating >= num}
            onClick={() => setRating(num)}
            aria-label={`Rating ${num} stars`}
          >
            <StarIcon fillColor="#6F3FD7" isFill={rating >= num} />
          </Star>
        ))}
      </div>
    </div>
  </div>
);

/**
 * One heading over two selects, so each select says its own piece rather than the heading carrying a
 * single message for both: told only that "More about you" is incomplete, a reviewer who has already
 * picked a skin type cannot tell which of the two boxes is still waiting. Both messages sit above the
 * row rather than inside the columns, which keeps the two selects level when only one is unanswered.
 * Each select is anchored separately so a refused submit scrolls to the one it stopped on.
 */
export const renderSkinInfoSection = ({
  skinType,
  setSkinType,
  skinConcern,
  setSkinConcern,
  skinTypeOptions,
  skinConcernOptions,
  skinTypeError,
  skinConcernError,
}: RenderSkinInfoSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full">
    <p className="text-label-medium font-medium">More about you*</p>
    {skinTypeError && <ReviewSubmissionAlert message={skinTypeError} />}
    {skinConcernError && <ReviewSubmissionAlert message={skinConcernError} />}
    <Row>
      <Section style={{ flex: 1, marginBottom: 0 }}>
        <div className="w-full" {...reviewFieldAnchor('skinType')}>
          <Select
            options={skinTypeOptions}
            value={skinType}
            onChange={setSkinType}
            title="Skin type"
            showSelectedValue
            floatingLabel
            width="100%"
          />
        </div>
      </Section>
      <Section style={{ flex: 1, marginBottom: 0 }}>
        <div className="w-full" {...reviewFieldAnchor('skinConcern')}>
          <Select
            options={skinConcernOptions}
            value={skinConcern}
            onChange={setSkinConcern}
            title="Skin concern"
            showSelectedValue
            floatingLabel
            width="100%"
          />
        </div>
      </Section>
    </Row>
  </div>
);

export const renderReviewContentSection = ({
  content,
  setContent,
  alertMessage = null,
  flaggedText,
  errorMessage,
}: RenderReviewContentSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full" {...reviewFieldAnchor('content')}>
    <p className="text-label-medium font-medium">Your review*</p>
    {/* An empty body cannot have been flagged, so the two alerts never actually stack. */}
    {errorMessage && <ReviewSubmissionAlert message={errorMessage} />}
    {alertMessage && <ReviewSubmissionAlert message={alertMessage} />}
    <ReviewBodyTextarea content={content} setContent={setContent} flaggedText={flaggedText} />
  </div>
);

export const renderTitleSection = ({ title, setTitle, errorMessage }: RenderTitleSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full" {...reviewFieldAnchor('title')}>
    <p className="text-label-medium font-medium">Add a headline*</p>
    {errorMessage && <ReviewSubmissionAlert message={errorMessage} />}
    <Input
      id="review-title"
      maxLength={200}
      value={title}
      onChange={(e) => setTitle(e.target.value)}
      placeholder="e.g. Super moisturizing, exactly what I needed"
    />
  </div>
);

/** The picked clip in the row it will occupy, with the encode running across it. */
const PendingVideoThumbnail = ({ pendingVideo }: { pendingVideo: PendingVideoUpload }) => {
  const percent = pendingVideo.progress === null ? null : Math.round(pendingVideo.progress * 100);

  return (
    <Thumbnail>
      <ReviewVideoPoster url={pendingVideo.posterUrl} className="w-full h-full object-cover rounded-lg" />
      <span
        className="absolute inset-0 flex flex-col justify-end gap-1 rounded-lg bg-generic-100/45 p-2"
        role="progressbar"
        aria-label="Compressing your video"
        aria-valuemin={0}
        aria-valuemax={100}
        {...(percent === null ? {} : { 'aria-valuenow': percent })}
      >
        <span className="text-label-xsmall font-medium text-generic-0">
          {percent === null ? 'Compressing…' : `${percent}%`}
        </span>
        {/* An encoder that has not reported yet gets a pulsing full bar: a bar frozen at zero reads
            as a stall, and a fake creeping one would be a number we do not have. */}
        <span className="block h-1 w-full overflow-hidden rounded-full bg-generic-0/40">
          <span
            className={`block h-full rounded-full bg-generic-0 ${percent === null ? 'w-full animate-pulse' : 'transition-[width] duration-200'}`}
            style={percent === null ? undefined : { width: `${percent}%` }}
          />
        </span>
      </span>
    </Thumbnail>
  );
};

/**
 * The label and the control it points at, kept together so the id never has to travel: two of these
 * forms can be mounted at once — the orders page overlays one over its list — and a shared id would
 * aim the second label at the first form's input. The area draws a plus icon and no text, so the
 * control names itself.
 */
const MediaPicker = ({
  onChange,
  disabled,
  isFirstAttachment,
}: {
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  disabled: boolean;
  isFirstAttachment: boolean;
}) => {
  const inputId = useId();
  const Area = isFirstAttachment ? UploadArea : SmallUploadArea;

  return (
    <Area htmlFor={inputId}>
      <PlusIcon />
      <FileInput
        id={inputId}
        type="file"
        accept={REVIEW_MEDIA_ACCEPT}
        multiple
        onChange={onChange}
        disabled={disabled}
        aria-label="Add photos or a video to your review"
      />
    </Area>
  );
};

export const renderImageUploadSection = ({
  images,
  imagePreviews,
  handleImageChange,
  handleRemoveImage,
  isProcessingImages = false,
  imageUploadError = null,
  maxAttachments = REVIEW_MEDIA_MAX_COUNT,
  processingMessage = 'Preparing your photo…',
  pendingVideo = null,
  errorMessage,
}: RenderImageUploadSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full" {...reviewFieldAnchor('images')}>
    <p className="text-label-medium font-medium">Add media</p>
    {errorMessage && <ReviewSubmissionAlert message={errorMessage} />}
    <ImageUploadBox>
      {imagePreviews.map((preview, idx) => (
        <Thumbnail key={preview.id}>
          {preview.isVideo ? (
            <ReviewVideoPoster url={preview.url} />
          ) : (
            <ThumbnailImage src={preview.url} alt={`Review photo ${idx + 1}`} />
          )}
          {/* A removal taken while a selection is in flight is undone when that append resolves
              against the list it captured, so the affordance waits rather than lying. */}
          <RemoveButton
            type="button"
            onClick={() => handleRemoveImage(preview.id)}
            aria-label="Remove attachment"
            disabled={isProcessingImages}
          >
            <LucideIcon name="X" width="14px" height="14px" className="text-generic-100" />
          </RemoveButton>
        </Thumbnail>
      ))}
      {pendingVideo && <PendingVideoThumbnail pendingVideo={pendingVideo} />}
      {images.length < maxAttachments && (
        <MediaPicker
          onChange={handleImageChange}
          disabled={isProcessingImages}
          isFirstAttachment={images.length === 0}
        />
      )}
    </ImageUploadBox>
    <p className="text-label-small font-normal text-neutral-500">
      * <span className="font-semibold text-neutral-700">Get extra credits</span> for attaching a photo or video.
    </p>
    {/* The clip's own tile is already saying this, and saying it twice reads as two things waiting. */}
    {isProcessingImages && !pendingVideo && (
      <p className="text-paragraph-xsmall text-neutral-600" role="status">
        {processingMessage}
      </p>
    )}
    {imageUploadError && (
      <p className="text-paragraph-xsmall text-error-600" role="alert">
        {imageUploadError}
      </p>
    )}
  </div>
);

export const renderRecommendationSection = ({
  recommend,
  setRecommend,
  errorMessage,
}: RenderRecommendationSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full" {...reviewFieldAnchor('recommend')}>
    <p className="text-label-medium font-medium">Repurchase intention*</p>
    {errorMessage && <ReviewSubmissionAlert message={errorMessage} />}

    <RadioGroup name="recommend" value={recommend} onValueChange={setRecommend}>
      <div className="flex flex-row gap-4">
        <RadioGroupItem value="yes">Yes</RadioGroupItem>

        <RadioGroupItem value="no">No</RadioGroupItem>
      </div>
    </RadioGroup>
  </div>
);

export const renderAgeGroupSection = ({ ageGroup, setAgeGroup, errorMessage }: RenderAgeGroupSectionProps) => (
  <div
    className="flex flex-col gap-3 items-start w-full"
    data-testid="review-age-group"
    {...reviewFieldAnchor('ageGroup')}
  >
    <p className="text-label-medium font-medium">Select age group*</p>
    {errorMessage && <ReviewSubmissionAlert message={errorMessage} />}
    {/* Figma sizes the field to its column rather than to the chosen label, so the box does not
        resize as the visitor changes bracket. */}
    <Select
      options={AGE_GROUP_OPTIONS}
      value={ageGroup}
      onChange={setAgeGroup}
      title="Age group"
      showSelectedValue
      floatingLabel
      width="100%"
    />
  </div>
);

export const renderGenderSection = ({ gender, setGender, errorMessage }: RenderGenderSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full" data-testid="review-gender" {...reviewFieldAnchor('gender')}>
    <p className="text-label-medium font-medium">Gender*</p>
    {errorMessage && <ReviewSubmissionAlert message={errorMessage} />}
    {/* Figma (5306-6766) draws plain radios here, not chips, and gives the row the height of the age
        select beside it so the two columns end level. */}
    <RadioGroup
      name="gender"
      value={gender}
      onValueChange={setGender}
      aria-label="Gender"
      className="flex flex-row flex-wrap items-center gap-4 min-h-12"
    >
      {GENDER_OPTIONS.map((option) => (
        <RadioGroupItem key={option.value} value={option.value}>
          {option.label}
        </RadioGroupItem>
      ))}
    </RadioGroup>
  </div>
);

export const renderNicknameSection = ({
  nickname,
  setNickname,
  isReadOnly = false,
  errorMessage,
}: RenderNicknameSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full" {...reviewFieldAnchor('nickname')}>
    <p className="text-label-medium font-medium">Display name*</p>
    {errorMessage && <ReviewSubmissionAlert message={errorMessage} />}
    <Input
      id="review-nickname"
      maxLength={25}
      value={nickname}
      onChange={(e) => setNickname(e.target.value)}
      // A signed-in reviewer publishes under their account name, and roughly a third of accounts
      // hold none — the server labels those reviews rather than rejecting them, so the empty field
      // says which label instead of reading as a value the reviewer forgot to fill in.
      placeholder={isReadOnly ? ANONYMOUS_REVIEWER_NAME : 'Enter your display name'}
      disabled={isReadOnly}
      className={isReadOnly ? 'bg-neutral-50 text-neutral-700' : undefined}
    />
  </div>
);

export const renderEmailSection = ({ email, setEmail, isReadOnly = false, errorMessage }: RenderEmailSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full" {...reviewFieldAnchor('email')}>
    <p className="text-label-medium font-medium">Email address*</p>
    {errorMessage && <ReviewSubmissionAlert message={errorMessage} />}
    <Input
      id="review-email"
      type="email"
      value={email}
      onChange={(e) => setEmail(e.target.value)}
      placeholder="Enter your email address"
      disabled={isReadOnly}
      className={isReadOnly ? 'bg-neutral-50 text-neutral-700' : undefined}
    />
  </div>
);

export const renderTermsSection = () => (
  <div className="flex flex-col gap-3 items-start w-full">
    {/* Figma marks this as a footnote with a leading asterisk, matching the media credit line, rather
        than as an info callout. */}
    <p className="text-label-small font-normal text-neutral-500">* Submitted reviews cannot be deleted.</p>
  </div>
);

export const renderSubmitSection = ({
  isSubmitDisabled,
  onSubmit,
  label = 'Submit review',
}: RenderSubmitSectionProps) => (
  <SubmitButton type="button" disabled={isSubmitDisabled} onClick={onSubmit}>
    {label}
  </SubmitButton>
);

/** Already published. Read-only: the update endpoint keeps them unless sent a retention list. */
export const renderExistingMediaSection = ({
  media,
  onRemove,
  isProcessingImages = false,
}: RenderExistingMediaSectionProps) => (
  <div className="flex flex-col gap-3 items-start w-full">
    <p className="text-label-medium font-medium">Attachments on this review</p>
    <div className="flex flex-row gap-2 items-center flex-wrap">
      {media.map(({ url, type }) => (
        <Thumbnail key={url}>
          {type === 'video' ? (
            <ReviewVideoPoster url={url} />
          ) : (
            <ThumbnailImage src={url} alt="" />
          )}
          <RemoveButton
            type="button"
            onClick={() => onRemove(url)}
            aria-label="Remove this attachment"
            disabled={isProcessingImages}
          >
            <LucideIcon name="X" width="14px" height="14px" className="text-generic-100" />
          </RemoveButton>
        </Thumbnail>
      ))}
    </div>
    <p className="text-label-small font-normal text-neutral-500">
      * Removed attachments disappear from your review once you save.
    </p>
  </div>
);

const formatCreditRange = ({ currency, fractionDigits, textCreditAmount, mediaCreditAmount }: ReviewCreditAmounts) => ({
  text: formatLoyaltyCredit(currency, textCreditAmount, fractionDigits),
  media: formatLoyaltyCredit(currency, mediaCreditAmount, fractionDigits),
});

interface CreditLineProps {
  iconName: 'LetterText' | 'Images';
  label: string;
  amount: string;
}

const CreditLine = ({ iconName, label, amount }: CreditLineProps) => (
  <span className="flex flex-row gap-2 items-center">
    <span className="flex flex-row gap-1 items-center">
      <LucideIcon name={iconName} width="16px" height="16px" className="text-neutral-400 shrink-0" />
      <span className="text-paragraph-small font-normal text-neutral-400 whitespace-nowrap">{label}</span>
    </span>
    <span className="text-paragraph-small font-medium text-primary-500 whitespace-nowrap">{amount}</span>
  </span>
);

interface ReviewRewardBannerProps {
  status: ReviewCreditBannerStatus;
}

/**
 * The amounts are fetched here rather than passed in — handed down, the product page dropped them
 * on the way. The verdict stays a prop: each surface asks a different question.
 */
export const ReviewRewardBanner = ({ status }: ReviewRewardBannerProps) => {
  const { currency } = useRegionCurrency();
  const { useGetReviewCreditAmounts } = useReviewService();
  const { data: creditAmounts } = useGetReviewCreditAmounts({ currency });

  const credit = creditAmounts && formatCreditRange(creditAmounts);
  const copy = buildReviewCreditBannerCopy(status, credit);
  const isEarning = status === 'eligible';

  return (
    <div
      className="flex flex-row gap-4 items-center w-full rounded-[8px] bg-primary-50 border border-primary-100 p-4"
      data-testid="review-reward-banner"
      data-status={status}
    >
      <LucideIcon name="Gift" width="24px" height="24px" className="text-primary-500 shrink-0 tablet:hidden" />
      <span className="hidden tablet:flex items-center justify-center w-14 h-14 shrink-0 rounded-full bg-generic-0">
        <LucideIcon name="Gift" width="24px" height="24px" className="text-primary-500" />
      </span>
      <div className="flex flex-col gap-1 items-start flex-1 min-w-0">
        {copy.wideHeadline ? (
          <>
            {/* Figma writes the headline in the singular on mobile and the plural on desktop. */}
            <p className="text-paragraph-medium font-medium text-neutral-700 tablet:hidden">{copy.headline}</p>
            <p className="hidden tablet:block text-paragraph-large font-medium text-neutral-700">{copy.wideHeadline}</p>
          </>
        ) : (
          <p className="text-paragraph-medium tablet:text-paragraph-large font-medium text-neutral-700">
            {copy.headline}
          </p>
        )}
        {/* Quoting a figure the server has not priced yet would promise the wrong amount in the
            reviewer's currency, so the lines wait rather than guessing. */}
        {isEarning && credit && (
          <div className="flex flex-col tablet:flex-row gap-1 tablet:gap-2 items-start tablet:items-center">
            <CreditLine iconName="LetterText" label="Text review :" amount={credit.text} />
            <span className="hidden tablet:block text-paragraph-small font-normal text-neutral-400">|</span>
            <CreditLine iconName="Images" label="Photo or Video review :" amount={credit.media} />
          </div>
        )}
        {copy.note && <p className="text-paragraph-small font-normal text-neutral-500">{copy.note}</p>}
      </div>
    </div>
  );
};

interface ProductReviewRewardBannerProps {
  /** Absent on the surfaces whose line item carries no product id; the offer stands rather than
      being withdrawn on a missing id. */
  productId?: number;
}

/** Read from the reader's orders, so a guest is answered without a call. */
export const ProductReviewRewardBanner = ({ productId }: ProductReviewRewardBannerProps) => {
  const { useGetUserMe } = useUserService();
  const { data: user, isPending: isSessionPending } = useGetUserMe();
  const isSignedIn = Boolean(user);
  const { useGetReviewCreditEligibility } = useReviewService();
  const { data: eligibility } = useGetReviewCreditEligibility(productId, isSignedIn);

  // Not yet answered is not signed out: rendering now would tell a member to sign in, then
  // swap the offer under them a moment later.
  if (isSessionPending) {
    return null;
  }

  return <ReviewRewardBanner status={resolveProductReviewCreditStatus({ isSignedIn, eligibility })} />;
};

/** Same offer, but nothing on the orders page is about one product — so it asks account-wide. */
export const OrdersReviewRewardBanner = () => {
  const { currency } = useRegionCurrency();
  const { useGetUserMe } = useUserService();
  const { data: user, isPending: isSessionPending } = useGetUserMe();
  const isSignedIn = Boolean(user);
  const { useGetReviewCreditSummary } = useReviewService();
  const { data: summary } = useGetReviewCreditSummary({ currency }, isSignedIn);

  if (isSessionPending) {
    return null;
  }

  return <ReviewRewardBanner status={resolveOrdersReviewCreditStatus({ isSignedIn, summary })} />;
};
